import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Box, Divider, Typography } from '@mui/material';
import AppleIcon from '@mui/icons-material/Apple';
import { CredentialResponse, GoogleLogin } from '@react-oauth/google';
import AppleSignin from 'react-apple-signin-auth';
import { useAuth } from '../../contexts/AuthContext';
import { getAppleOAuthClientId, getAppleRedirectUri, getGoogleOAuthClientId } from '../../config/oauth';
import {
  appleLogin,
  appleUserIdFromIdToken,
  googleLogin2,
  SocialLoginNeedsBirthday,
  SocialLoginSuccess,
} from '../../services/socialAuthApi';
import AuthSocialButton from './AuthSocialButton';
import GoogleColorIcon from './GoogleColorIcon';
import SocialBirthdayDialog, { SocialBirthdayContext } from './SocialBirthdayDialog';
import { authScreen } from './authScreenStyles';
import { useT } from '../../i18n/useT';

type AppleSuccessResponse = {
  authorization?: { id_token?: string };
  user?: {
    email?: string;
    name?: { firstName?: string; lastName?: string };
  };
};

type Props = {
  onSuccess: () => void;
  onError: (message: string) => void;
  disabled?: boolean;
  tone?: 'dark' | 'marketing';
};

export default function SocialLoginSection({ onSuccess, onError, disabled, tone = 'dark' }: Props) {
  const t = useT();
  const { establishSession } = useAuth();
  const googleClientId = getGoogleOAuthClientId();
  const appleClientId = getAppleOAuthClientId();
  const googleHostRef = useRef<HTMLDivElement>(null);

  const [birthdayOpen, setBirthdayOpen] = useState(false);
  const [birthdayContext, setBirthdayContext] = useState<SocialBirthdayContext | null>(null);

  const finishLogin = useCallback(
    (result: SocialLoginSuccess) => {
      establishSession({
        id: result.userId,
        token: result.token,
        email: result.email,
        firstName: result.firstName,
        lastName: result.lastName,
      });
      onSuccess();
    },
    [establishSession, onSuccess]
  );

  const openBirthdayFlow = useCallback((needs: SocialLoginNeedsBirthday, provider: 'google' | 'apple') => {
    setBirthdayContext({ provider, email: needs.email, appleId: needs.appleId });
    setBirthdayOpen(true);
  }, []);

  const handleGoogleSuccess = useCallback(
    async (credentialResponse: CredentialResponse) => {
      const credential = credentialResponse.credential;
      if (!credential) {
        onError(t('socialLoginFailed'));
        return;
      }
      try {
        const payload = JSON.parse(atob(credential.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))) as {
          email?: string;
          given_name?: string;
          family_name?: string;
        };
        const email = payload.email || '';
        if (!email) {
          onError(t('socialLoginEmailRequired'));
          return;
        }
        const result = await googleLogin2({
          email,
          name: payload.given_name || 'User',
          lastName: payload.family_name || '',
        });
        if (result.kind === 'success') finishLogin(result);
        else if (result.kind === 'needs_birthday') openBirthdayFlow(result, 'google');
        else onError(result.message);
      } catch {
        onError(t('socialLoginFailed'));
      }
    },
    [finishLogin, onError, openBirthdayFlow, t]
  );

  const handleAppleSuccess = useCallback(
    async (response: AppleSuccessResponse) => {
      const idToken = response.authorization?.id_token;
      if (!idToken) {
        onError(t('socialLoginFailed'));
        return;
      }
      try {
        const appleId = appleUserIdFromIdToken(idToken);
        const email = response.user?.email || '';
        const result = await appleLogin({
          appleId,
          email,
          name: response.user?.name?.firstName || '',
          lastName: response.user?.name?.lastName || '',
        });
        if (result.kind === 'success') finishLogin(result);
        else if (result.kind === 'needs_birthday')
          openBirthdayFlow({ ...result, appleId: result.appleId || appleId, email: result.email || email }, 'apple');
        else onError(result.message);
      } catch {
        onError(t('socialLoginFailed'));
      }
    },
    [finishLogin, onError, openBirthdayFlow, t]
  );

  const triggerGoogle = useCallback(() => {
    const host = googleHostRef.current;
    if (!host) return;
    const btn = host.querySelector('[role="button"]') ?? host.querySelector('div[tabindex="0"]');
    if (btn instanceof HTMLElement) btn.click();
  }, []);

  const showGoogle = Boolean(googleClientId);
  const showApple = Boolean(appleClientId);

  const appleAuthOptions = useMemo(
    () => ({
      clientId: appleClientId,
      scope: 'email name',
      redirectURI: getAppleRedirectUri(),
      state: 'visime-web',
      usePopup: true,
    }),
    [appleClientId]
  );

  const marketing = tone === 'marketing';

  if (!showGoogle && !showApple) return null;

  return (
    <Box sx={{ opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
      <Divider sx={{ my: 2.25, borderColor: marketing ? 'var(--l-line)' : authScreen.glassBorder }}>
        <Typography
          sx={{
            fontSize: '0.6875rem',
            fontWeight: 500,
            color: marketing ? 'var(--l-ink-3)' : authScreen.textDim,
            fontFamily: marketing ? 'var(--l-font)' : authScreen.font,
            px: 1,
          }}
        >
          {t('loginOrContinueWith')}
        </Typography>
      </Divider>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {showGoogle && (
          <>
            <Box ref={googleHostRef} aria-hidden sx={{ position: 'fixed', left: -9999, opacity: 0, pointerEvents: 'none' }}>
              <GoogleLogin
                onSuccess={(res) => void handleGoogleSuccess(res)}
                onError={() => onError(t('socialLoginFailed'))}
                theme="outline"
                size="large"
                text="continue_with"
              />
            </Box>
            <AuthSocialButton icon={<GoogleColorIcon />} onClick={triggerGoogle} disabled={disabled} tone={tone}>
              {t('loginContinueGoogle')}
            </AuthSocialButton>
          </>
        )}
        {showApple && (
          <AppleSignin
            uiType="dark"
            authOptions={appleAuthOptions}
            onSuccess={(response: AppleSuccessResponse) => void handleAppleSuccess(response)}
            onError={() => onError(t('socialLoginFailed'))}
            render={(props: React.ButtonHTMLAttributes<HTMLButtonElement>) => (
              <AuthSocialButton icon={<AppleIcon sx={{ fontSize: 20 }} />} disabled={disabled} buttonProps={props} tone={tone}>
                {t('loginContinueApple')}
              </AuthSocialButton>
            )}
          />
        )}
      </Box>

      <SocialBirthdayDialog
        open={birthdayOpen}
        context={birthdayContext}
        onClose={() => {
          setBirthdayOpen(false);
          setBirthdayContext(null);
        }}
        onComplete={(result) => {
          setBirthdayOpen(false);
          setBirthdayContext(null);
          finishLogin(result);
        }}
      />
    </Box>
  );
}
